
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface MuxPlayerProps {
  playbackId: string;
  title?: string;
  className?: string;
  autoplay?: boolean;
  aspectRatio?: string;
  metadata?: {
    video_title: string;
    player_name: string;
  };
}

const MuxPlayer: React.FC<MuxPlayerProps> = ({
  playbackId,
  title,
  className,
  autoplay = false,
  aspectRatio = "16/9",
  metadata
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [playerLoaded, setPlayerLoaded] = useState(false);

  useEffect(() => {
    // Load Mux Player script if not already loaded
    if (!document.querySelector('script[src*="mux-player"]')) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@mux/mux-player';
      script.async = true;
      
      script.onload = () => {
        setPlayerLoaded(true);
      };
      
      document.body.appendChild(script);
      
      return () => {
        // Only remove the script if this component added it
        const scriptElement = document.querySelector('script[src*="mux-player"]');
        if (scriptElement) {
          document.body.removeChild(scriptElement);
        }
      };
    } else {
      setPlayerLoaded(true);
    }
  }, []);

  const handlePlayClick = () => {
    setIsPlaying(true);
    
    // Find and play the mux-player element
    if (containerRef.current) {
      const muxPlayer = containerRef.current.querySelector('mux-player');
      if (muxPlayer) {
        // @ts-ignore - mux player has a play method
        muxPlayer.play();
      }
    }
  };

  // Create HTML for the mux-player element
  const createMuxPlayerHTML = () => {
    const videoTitle = metadata?.video_title || title || "Everhouse Video";
    
    return `
      <mux-player
        playback-id="${playbackId}"
        metadata-video-title="${videoTitle}"
        stream-type="on-demand"
        primary-color="#ffffff"
        secondary-color="#000000"
        accent-color="#adf5ff"
        autoplay="${autoplay ? "true" : "false"}"
        style="height: 100%; width: 100%; object-fit: cover;"
      ></mux-player>
    `;
  };

  return (
    <div 
      className={cn("relative overflow-hidden rounded-md", className)} 
      ref={containerRef}
      style={{ aspectRatio }}
    >
      <div className="absolute inset-0 w-full h-full bg-black">
        {/* Use dangerouslySetInnerHTML to insert the custom element */}
        <div
          className="w-full h-full"
          dangerouslySetInnerHTML={{
            __html: createMuxPlayerHTML()
          }}
        />
      </div>
      
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <Button
            variant="play"
            size="play"
            onClick={handlePlayClick}
            aria-label="Play video"
          >
            <img 
              src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" 
              alt="Play" 
              className="w-10 h-10 md:w-12 md:h-12"
            />
          </Button>
        </div>
      )}
    </div>
  );
};

export default MuxPlayer;
